import MultiCurrency from '@assets/images/custom/multiCurrency.svg';

import type { Statistics } from '@interfaces/stats';
import { CurrencyCodes } from '@onevesthq/ov-enums';
import { UserContext } from '@providers/userContextProvider';
import { formatMoneyValue } from '@utils/moneyFormatting';
import {
  Box,
  Stack,
  Tooltip,
  Typography,
  type TypographyProps,
  useTheme,
} from 'hephaestus/web';
import { type ReactElement, useContext, useEffect, useState } from 'react';

type GroupedMarketValue = {
  currency: CurrencyCodes;
  marketValueCents: number;
};

type CurrencyBreakdownTooltipProps = {
  lines: GroupedMarketValue[];
  titleSx?: TypographyProps['sx'];
  children: ReactElement;
};

export const CurrencyBreakdownTooltip = ({
  lines,
  titleSx,
  children,
}: CurrencyBreakdownTooltipProps) => {
  const theme = useTheme();

  if (lines.length === 0) {
    return children;
  }

  return (
    <Tooltip
      title={
        <Stack>
          {lines.map((elem) => (
            <Typography
              key={elem.currency}
              variant='trimmedSmall'
              sx={[
                { color: theme.color.surface },
                ...(Array.isArray(titleSx)
                  ? titleSx
                  : titleSx != null
                    ? [titleSx]
                    : []),
              ]}
            >
              {`${elem.currency} ${formatMoneyValue(elem.marketValueCents, '', 2, false)}`}
            </Typography>
          ))}
        </Stack>
      }
      placement='top'
      slotProps={{ tooltip: { 'data-testid': 'multi-currency-tooltip' } }}
    >
      {children}
    </Tooltip>
  );
};

type CurrencyIndicatorProps = {
  titleSx?: TypographyProps['sx'];
  statistics?: Statistics;
  useExternalStatisticsEnabled?: boolean;
  readFromSnapshot?: boolean;
};

export const CurrencyIndicator = ({
  titleSx,
  statistics,
  useExternalStatisticsEnabled,
  readFromSnapshot = false,
}: CurrencyIndicatorProps) => {
  const { activeCurrency } = useContext(UserContext);
  const [groupedMarketValue, setGroupedMarketValue] = useState<
    GroupedMarketValue[]
  >([]);

  useEffect(() => {
    const holdings = readFromSnapshot
      ? statistics?.historySnapshot?.holdings
      : statistics?.holdings;
    if (
      !useExternalStatisticsEnabled &&
      holdings &&
      holdings.some(
        (holding) => holding.originalCurrency?.currency !== activeCurrency,
      )
    ) {
      const marketValueByCurrency = holdings.reduce<GroupedMarketValue[]>(
        (acc, holding) => {
          if (holding.originalCurrency) {
            const { originalCurrency } = holding;
            const currencyHolding = acc.find(
              (elem) => elem.currency === originalCurrency.currency,
            );
            if (currencyHolding) {
              currencyHolding.marketValueCents +=
                originalCurrency.totalCents ?? 0;
            } else {
              acc.push({
                currency: originalCurrency.currency ?? CurrencyCodes.CAD,
                marketValueCents: originalCurrency.totalCents ?? 0,
              });
            }
          }
          return acc;
        },
        [],
      );
      marketValueByCurrency.sort(sortMarketValueByCurrency);
      setGroupedMarketValue(marketValueByCurrency);
    } else if (
      useExternalStatisticsEnabled &&
      statistics?.originalCurrencies?.some(
        (elem) => elem.currency !== activeCurrency,
      )
    ) {
      const marketValueByCurrency = statistics.originalCurrencies;
      marketValueByCurrency?.sort(sortMarketValueByCurrency);
      setGroupedMarketValue(marketValueByCurrency);
    }
  }, [
    statistics,
    activeCurrency,
    useExternalStatisticsEnabled,
    readFromSnapshot,
  ]);

  if (groupedMarketValue.length === 0) {
    return null;
  }

  return (
    <CurrencyBreakdownTooltip lines={groupedMarketValue} titleSx={titleSx}>
      <Box
        sx={{ width: '20px', height: '20px', flexShrink: 0 }}
        data-testid='multi-currency-container'
      >
        <img
          src={MultiCurrency}
          alt='multi-currency'
          style={{ width: '20px', height: '20px' }}
          data-testid='multi-currency-image'
        />
      </Box>
    </CurrencyBreakdownTooltip>
  );
};

type CurrencyValue = Pick<GroupedMarketValue, 'currency'>;

const sortMarketValueByCurrency = (a: CurrencyValue, b: CurrencyValue) => {
  if (a.currency > b.currency) return 1;
  return a.currency < b.currency ? -1 : 0;
};
